/**
 * The two things a player can do on the reveal surface, across the two routes
 * it lives on.
 *
 * Both are offered for the whole of the reveal, because the reveal and the
 * explanation are one screen (DESIGN.md §3.6). The server disagrees for the
 * first few seconds: during `reveal` it refuses `thumbs_vote` ("voting happens
 * during the justification") and `justification_done` ("the reveal is still
 * running"), and an error toast is a terrible answer to a control the player was
 * invited to use.
 *
 * So an early press is held and sent the instant the phase lands. The control
 * shows pressed either way, each intent is sent exactly once, and the player
 * never learns that there were two screens.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ThumbsVote } from '@idgame/protocol';
import {
  drainPending,
  recallIntents,
  recordEndTurn,
  recordVote,
  turnKey,
} from './turnMemory.js';

export interface TurnActions {
  vote: ThumbsVote | null;
  castVote: (vote: ThumbsVote) => void;
  endRequested: boolean;
  endTurn: () => void;
}

export interface TurnActionsInput {
  turnIndex: number;
  questionNumber: number;
  /** True once the server will accept these (phase `justification`). */
  live: boolean;
  sendVote: (vote: ThumbsVote) => void;
  sendEndTurn: () => void;
}

export function useTurnActions({
  turnIndex,
  questionNumber,
  live,
  sendVote,
  sendEndTurn,
}: TurnActionsInput): TurnActions {
  const key = turnKey(turnIndex, questionNumber);
  const [intents, setIntents] = useState(() => recallIntents(key));

  // The state is per-route; the memory is per-turn. Re-read on the way in so
  // the second route starts where the first one left off.
  useEffect(() => setIntents(recallIntents(key)), [key]);

  useEffect(() => {
    if (!live) return;
    const pending = drainPending(key);
    if (pending.vote) sendVote(pending.vote);
    if (pending.end) sendEndTurn();
  }, [live, key, sendVote, sendEndTurn]);

  const castVote = useCallback(
    (vote: ThumbsVote) => {
      if (recallIntents(key).vote) return;
      recordVote(key, vote, live);
      setIntents(recallIntents(key));
      if (live) sendVote(vote);
    },
    [key, live, sendVote],
  );

  const endTurn = useCallback(() => {
    if (recallIntents(key).endRequested) return;
    recordEndTurn(key, live);
    setIntents(recallIntents(key));
    if (live) sendEndTurn();
  }, [key, live, sendEndTurn]);

  return { vote: intents.vote, castVote, endRequested: intents.endRequested, endTurn };
}
